import React, { useMemo, useState } from "react";
import { Formik, FormikHelpers } from "formik";
import { useQuery } from "@tanstack/react-query";
import * as Yup from "yup";
import toast from "react-hot-toast";

import Button from "components/atoms/button/button.atom";
import adminApi from "services/admin-api";
import {
  AthleteFormFieldInput,
  AthleteFormFieldLabel,
  AthleteFormFieldSelect,
  AthleteFormFormLayout,
  AthleteFormPreviewImage,
  AthleteFormScrollArea,
  AthleteFormSection,
  AthleteFormTextBlock,
  AthleteFormTextBlockSecondary,
} from "components/molecules/forms/athlete-form/styles/athlete-form.inline.styles";

interface AthleteFormValues {
  first_name: string;
  last_name: string;
  position: string;
  country: string;
  in_app_credits: number | string;
  sport_id?: string;
  team_ids?: string[];
  image?: string;
}

interface AthleteFormProps {
  initialValues: AthleteFormValues;
  onSubmit: (values: AthleteFormValues) => Promise<void> | void;
  onCancel?: () => void;
}

const AthleteValidationSchema = Yup.object().shape({
  first_name: Yup.string().trim().required("First name is required."),
  last_name: Yup.string().trim().required("Last name is required."),
  position: Yup.string().trim().required("Position is required."),
  country: Yup.string().trim().required("Country is required."),
  in_app_credits: Yup.number()
    .typeError("Credits must be a number.")
    .min(0, "Credits cannot be negative.")
    .required("Credits are required."),
  sport_id: Yup.string().required("Sport is required."),
  team_ids: Yup.array().of(Yup.string()).min(1, "Select at least one team."),
  image: Yup.string().url("Invalid image URL").nullable(),
});

export const AthleteForm: React.FC<AthleteFormProps> = ({ initialValues, onSubmit, onCancel }) => {
  const [previewUrl, setPreviewUrl] = useState(initialValues.image ?? "");
  const [selectedSport, setSelectedSport] = useState(initialValues.sport_id ?? "");

  const { data: sports = [] } = useQuery({
    queryKey: ["sports", "athlete-form"],
    queryFn: () => adminApi.sports.list(),
  });
  const { data: countries = [] } = useQuery({
    queryKey: ["countries", "athlete-form"],
    queryFn: () => adminApi.countries.list(),
  });
  const { data: teams = [] } = useQuery({
    queryKey: ["teams", "athlete-form", selectedSport],
    queryFn: () => adminApi.teams.list(selectedSport ? { sport_id: selectedSport } : undefined),
    enabled: Boolean(selectedSport),
  });

  const sportsOptions = useMemo(
    () =>
      [...sports]
        .map((sport: any) => ({
          label: sport.name || "Unknown sport",
          value: String(sport.id),
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [sports],
  );

  const teamOptions = useMemo(
    () =>
      [...teams]
        .map((team: any) => ({
          label: team.name || "Unknown team",
          value: String(team.id),
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [teams],
  );

  const countryOptions = useMemo(
    () =>
      [...countries]
        .map((country: any) => String(country.name || ""))
        .filter((countryName) => countryName.trim().length > 0)
        .sort((left, right) => left.localeCompare(right)),
    [countries],
  );

  const handleSubmit = async (
    values: AthleteFormValues,
    actions: FormikHelpers<AthleteFormValues>,
  ) => {
    try {
      await onSubmit({
        ...values,
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        position: values.position.trim(),
        country: values.country.trim(),
        image: values.image?.trim() || "",
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Error saving athlete");
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={AthleteValidationSchema}
    >
      {({ errors, handleChange, handleSubmit, isSubmitting, setFieldValue, values }) => (
        <AthleteFormFormLayout onSubmit={handleSubmit}>
          <AthleteFormScrollArea>
            <div>
              <AthleteFormFieldLabel htmlFor="athlete-first-name">First name</AthleteFormFieldLabel>
              <AthleteFormFieldInput
                id="athlete-first-name"
                name="first_name"
                onChange={handleChange}
                placeholder="First name"
                value={values.first_name}
              />
              {errors.first_name ? <AthleteFormTextBlock>{errors.first_name}</AthleteFormTextBlock> : null}
            </div>

            <div>
              <AthleteFormFieldLabel htmlFor="athlete-last-name">Last name</AthleteFormFieldLabel>
              <AthleteFormFieldInput
                id="athlete-last-name"
                name="last_name"
                onChange={handleChange}
                placeholder="Last name"
                value={values.last_name}
              />
              {errors.last_name ? <AthleteFormTextBlock>{errors.last_name}</AthleteFormTextBlock> : null}
            </div>

            <div>
              <AthleteFormFieldLabel htmlFor="athlete-position">Position</AthleteFormFieldLabel>
              <AthleteFormFieldInput
                id="athlete-position"
                name="position"
                onChange={handleChange}
                placeholder="Forward, Midfielder, Goalkeeper"
                value={values.position}
              />
              {errors.position ? <AthleteFormTextBlock>{errors.position}</AthleteFormTextBlock> : null}
            </div>

            <div>
              <AthleteFormFieldLabel htmlFor="athlete-country">Country</AthleteFormFieldLabel>
              <AthleteFormFieldSelect
                id="athlete-country"
                name="country"
                onChange={handleChange}
                value={values.country}
              >
                <option value="">Select country</option>
                {countryOptions.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </AthleteFormFieldSelect>
              {errors.country ? <AthleteFormTextBlock>{errors.country}</AthleteFormTextBlock> : null}
            </div>

            <div>
              <AthleteFormFieldLabel htmlFor="athlete-credits">Credits (Kudu)</AthleteFormFieldLabel>
              <AthleteFormFieldInput
                id="athlete-credits"
                min={0}
                name="in_app_credits"
                onChange={handleChange}
                placeholder="0"
                type="number"
                value={values.in_app_credits}
              />
              {errors.in_app_credits ? (
                <AthleteFormTextBlock>{String(errors.in_app_credits)}</AthleteFormTextBlock>
              ) : null}
            </div>

            <div>
              <AthleteFormFieldLabel htmlFor="athlete-sport">Sport</AthleteFormFieldLabel>
              <AthleteFormFieldSelect
                id="athlete-sport"
                name="sport_id"
                onChange={(event) => {
                  handleChange(event);
                  setSelectedSport(event.target.value);
                  setFieldValue("team_ids", []);
                }}
                value={values.sport_id || ""}
              >
                <option value="">Select sport</option>
                {sportsOptions.map((sport) => (
                  <option key={sport.value} value={sport.value}>
                    {sport.label}
                  </option>
                ))}
              </AthleteFormFieldSelect>
              {errors.sport_id ? <AthleteFormTextBlock>{errors.sport_id}</AthleteFormTextBlock> : null}
            </div>

            <div>
              <AthleteFormFieldLabel htmlFor="athlete-teams">Teams</AthleteFormFieldLabel>
              <AthleteFormFieldSelect
                id="athlete-teams"
                multiple
                name="team_ids"
                onChange={(event) => {
                  const selected = Array.from(event.target.selectedOptions, (option) => option.value);
                  setFieldValue("team_ids", selected);
                }}
                value={values.team_ids || []}
              >
                {teamOptions.map((team) => (
                  <option key={team.value} value={team.value}>
                    {team.label}
                  </option>
                ))}
              </AthleteFormFieldSelect>
              {errors.team_ids ? <AthleteFormTextBlock>{String(errors.team_ids)}</AthleteFormTextBlock> : null}
              <AthleteFormTextBlockSecondary>
                {selectedSport
                  ? "Hold Ctrl (Cmd) to select multiple teams."
                  : "Select a sport first to load teams."}
              </AthleteFormTextBlockSecondary>
            </div>

            <div>
              <AthleteFormFieldLabel htmlFor="athlete-image">Profile image URL</AthleteFormFieldLabel>
              <AthleteFormFieldInput
                id="athlete-image"
                name="image"
                onChange={(event) => {
                  handleChange(event);
                  setPreviewUrl(event.target.value);
                }}
                placeholder="https://example.com/athlete-image.jpg"
                value={values.image ?? ""}
              />
              {errors.image ? <AthleteFormTextBlock>{errors.image}</AthleteFormTextBlock> : null}
              {previewUrl ? (
                <AthleteFormPreviewImage
                  alt={
                    `${values.first_name || "Athlete"} ${values.last_name || ""}`.trim() || "Preview"
                  }
                  fallbackLabel={
                    `${values.first_name || ""} ${values.last_name || ""}`.trim() || "Athlete preview"
                  }
                  src={previewUrl}
                  variant="athlete"
                />
              ) : null}
            </div>
          </AthleteFormScrollArea>

          <AthleteFormSection>
            <Button onClick={onCancel} text="Cancel" variant="outlined" width={120} />
            <Button
              backgroundColor="#E1AD01"
              isLoading={isSubmitting}
              onClick={handleSubmit}
              text={initialValues.first_name || initialValues.last_name ? "Save" : "Add athlete"}
              width={140}
            />
          </AthleteFormSection>
        </AthleteFormFormLayout>
      )}
    </Formik>
  );
};
